'use client';

import * as React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import { devWarn, IS_PROD } from '../internal/devWarn';
import { usePopover, type UsePopoverReturn } from './Popover';
import { JIcon } from '../../icons';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeProps } from '../escape-hatches';

/* ── Canonical `isApplied` predicate (round-1 amendment A3) ──
   Single source of truth for "is this filter applied?" — derived
   inside the component / hook and surfaced as the `data-applied`
   attribute on `.j-filter-btn`. Sentinel `null` / `undefined` /
   empty-string / `0` / `false` entries inside an array value are
   normalized **out** via `filter(Boolean)`. See spec §2 boundary
   matrix and test #4. */
function computeIsApplied(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.filter(Boolean).length > 0;
  }
  return Boolean(value);
}

/* Stable normalization of `value` into a `readonly unknown[]` so the
   default aria-label composer + count rendering share one source.
   Non-array values become a single-entry array post-truthy-filter.
   Empty / all-falsy collapses to []. */
function normalizeValues(value: unknown): readonly unknown[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

/* ── Allowed rest-prop pass-through (mirrors Card's pickAllowed) ──
   The kit-wide StyledProps contract exposes `className` only
   (`src/kit/types.ts:10-12`); inline-CSS forwarding must go through
   `unsafe_style` per ADR-017. A typed-bag spread could smuggle
   `style`, `role`, `tabIndex`, `dangerouslySetInnerHTML`, or other
   reserved keys at runtime. `pickAllowed` filters the spread on the
   way out so the controlled a11y contract holds regardless of caller
   discipline. Test #25 asserts adversarial spread is dropped. */
type AllowedRest = Pick<
  React.HTMLAttributes<HTMLButtonElement>,
  'id' | 'title' | 'lang' | 'dir'
> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};

const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);

function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

/* ── Default English aria-label composer ────────────────
   Constructs "Brand, filtered by Nike and Adidas" / "Brand, filtered
   by Nike, Adidas, and 2 more" per spec §7. Adopters serving non-
   English locales MUST pass a localized composer; dev-warn fires once
   per session when `document.documentElement.lang` is non-`en*`. */
function defaultAriaLabelComposer(label: string, values: readonly unknown[]): string {
  if (values.length === 0) return label;
  const strs = values.map((v) => String(v));
  if (strs.length === 1) return `${label}, filtered by ${strs[0]}`;
  if (strs.length === 2) return `${label}, filtered by ${strs[0]} and ${strs[1]}`;
  const visible = strs.slice(0, 2);
  const moreCount = strs.length - 2;
  return `${label}, filtered by ${visible[0]}, ${visible[1]}, and ${moreCount} more`;
}

/* ── Once-per-session devWarn dedupe ────────────────────
   Module-scoped Set keyed on the spec's stable `'jkit.filter-button.*'`
   keys. Mirrors Card's `_warnedHostileAria` pattern. Tests reset via
   `_resetDevWarnKeysForTest` (internal export).

   Defer via `queueMicrotask` (spec §11): synchronous warns during
   render break SSR (`renderToString` would emit warns into the server
   log). In SSR the microtask queue doesn't drain before
   `renderToString` returns, so the warn silently no-ops on the
   server. In the browser, the warn fires on the next tick. The
   `typeof queueMicrotask` guard falls back to `setTimeout` for
   environments without `queueMicrotask` (older Node, some edge
   runtimes). */
const _warnedKeys = new Set<string>();
function devWarnOnce(key: string, message: string): void {
  if (IS_PROD) return;
  if (_warnedKeys.has(key)) return;
  _warnedKeys.add(key);
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(() => devWarn(message));
  } else {
    setTimeout(() => devWarn(message), 0);
  }
}

/** @internal Reset the once-per-session warn dedupe set. Test-only. */
export function _resetFilterButtonWarnDedupeForTest(): void {
  _warnedKeys.clear();
}

/* ── valueDisplay rule ─────────────────────────────────
   Smart defaults per spec §5 + ADR-030 §Value display rule.
     0  → 'none'   (label only — chip is in rest state)
     1-2 → 'inline'
     ≥3  → 'count'
   Explicit `valueDisplay` prop overrides; `renderValue` overrides
   everything. */
export type FilterButtonValueDisplay = 'inline' | 'count' | 'none' | 'auto';

function resolveValueDisplay(
  explicit: FilterButtonValueDisplay | undefined,
  values: readonly unknown[],
): 'inline' | 'count' | 'none' {
  if (explicit && explicit !== 'auto') return explicit;
  if (values.length === 0) return 'none';
  if (values.length <= 2) return 'inline';
  return 'count';
}

/* ── useFilterButton hook (spec §3) ────────────────────
   Thin wrapper over usePopover. Composes with `panelRole: 'dialog'`
   (default) and **reuses** the returned `popoverId` — does NOT mint a
   new id. Single source of truth for `aria-controls` ↔ panel `id`.

   Owns focus return to the trigger on popover close (spec §3 "Focus
   return on popover close"): mirrors Modal.tsx:160-180's detached-
   node-guarded `previouslyFocused.focus({ preventScroll: true })`
   recipe. NOTE: Popover.tsx (PR #151) ALSO restores focus on Escape /
   outside-click — the hook's recipe here is defensive belt-and-braces
   for the programmatic-close path (`filter.close()`) which Popover's
   handler does not cover. */

export interface UseFilterButtonOptions {
  value?: unknown;
  onClear?: () => void;
  /** ARIA role for the paired popover panel. Defaults to `'dialog'`;
   *  pass `'menu'` when the popover content is a pure menu tree; pass
   *  `null` to suppress `aria-haspopup` (escape hatch for custom ARIA
   *  stories). */
  panelRole?: 'dialog' | 'menu' | null;
  /** Localized aria-label composer. Receives `(label, normalizedValues)`
   *  where `normalizedValues` is the post-`filter(Boolean)` array. */
  ariaLabelComposer?: (label: string, values: readonly unknown[]) => string;
}

/** @internal Hook-resolved metadata. Spread onto `<FilterButton>` via
 *  `filterButtonProps` so the component can reconcile its own
 *  `value` / `onClear` props against the hook's source-of-truth and
 *  dev-warn on divergence (spec §11 `meta-mismatch`). */
export interface FilterButtonMeta {
  value: unknown;
  isApplied: boolean;
  onClear?: () => void;
  ariaLabelComposer?: (label: string, values: readonly unknown[]) => string;
}

export interface UseFilterButtonReturn<TAnchor extends HTMLElement = HTMLButtonElement> {
  filterButtonProps: {
    'aria-haspopup'?: 'dialog' | 'menu';
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
    /* Click handler is `(e) => void` (spec §3) so adopter handlers
       spread onto the same element compose without TS strict-mode
       errors. `usePopover()`'s `anchorProps.onClick` is `() => void`
       (it doesn't read the event arg); we wrap it to accept the
       event so the published shape matches the spec. */
    onClick: (e: React.MouseEvent<TAnchor>) => void;
    /* `usePopover()` returns a `RefObject<TAnchor>` from `useRef<T>(null)`
       (single source of truth for ref identity shared with the paired
       Popover panel). FilterButton reuses it verbatim; the published
       type matches that shape. Spec §3 previously documented
       `RefCallback`; that was a leftover from an earlier round and is
       superseded by this `RefObject` shape (see PR follow-up note). */
    ref: React.RefObject<TAnchor>;
    /* @internal Hook-resolved metadata used by `<FilterButton>` to
       reconcile against the adopter's component-side props
       (spec §11 `jkit.filter-button.meta-mismatch`). */
    _filterMeta: FilterButtonMeta;
  };
  popoverProps: UsePopoverReturn<TAnchor>['popoverProps'];
  open: () => void;
  close: () => void;
  isOpen: boolean;
  popoverId: string;
  /** @internal Same as `filterButtonProps._filterMeta` — also surfaced
   *  on the hook return for adopters who need to inspect the resolved
   *  metadata without spreading. */
  _filterMeta: FilterButtonMeta;
}

export function useFilterButton<TAnchor extends HTMLElement = HTMLButtonElement>(
  options: UseFilterButtonOptions = {},
): UseFilterButtonReturn<TAnchor> {
  const { value, onClear, panelRole = 'dialog', ariaLabelComposer } = options;

  /* Compose usePopover with the requested panelRole. usePopover treats
     `null` as "no aria-haspopup on the trigger"; we pass null through. */
  /* usePopover supports panelRole 'dialog' | 'tooltip' | 'menu' | null;
     FilterButton's surface narrows that to 'dialog' | 'menu' | null. */
  const pop = usePopover<TAnchor>({
    panelRole: panelRole as 'dialog' | 'menu' | null | undefined,
  });

  /* Programmatic-close focus return (defensive — Popover.tsx already
     restores focus on Escape + outside-click). Wraps the original
     close so adopters calling `filter.close()` get the same focus-
     return semantics as keyboard / pointer dismissal. */
  const popClose = pop.close;
  const close = React.useCallback(() => {
    popClose();
    /* Defer to next microtask so React has flushed any pending
       unmount before we attempt to focus the anchor. */
    queueMicrotask(() => {
      const anchor = pop.anchorRef.current;
      if (anchor && typeof anchor.focus === 'function' && document.contains(anchor)) {
        try {
          anchor.focus({ preventScroll: true });
        } catch {
          /* noop — some elements refuse focus in certain states. */
        }
      }
    });
  }, [popClose, pop.anchorRef]);

  const isApplied = computeIsApplied(value);

  /* Wrap `pop.anchorProps.onClick` (which is `() => void`) into a
     `(e) => void` shape so the published prop type matches spec §3.
     Popover's handler doesn't read the event arg, so we forward
     without it. Stable identity via useCallback so the published
     prop reference is stable across renders. */
  const popOnClick = pop.anchorProps.onClick;
  const onClick = React.useCallback(
    (_e: React.MouseEvent<TAnchor>) => {
      popOnClick();
    },
    [popOnClick],
  );

  const _filterMeta: FilterButtonMeta = { value, isApplied, onClear, ariaLabelComposer };

  return {
    filterButtonProps: {
      'aria-haspopup': pop.anchorProps['aria-haspopup'],
      'aria-expanded': pop.anchorProps['aria-expanded'],
      'aria-controls': pop.anchorProps['aria-controls'],
      onClick,
      ref: pop.anchorRef,
      _filterMeta,
    },
    popoverProps: pop.popoverProps,
    open: pop.open,
    close,
    isOpen: pop.isOpen,
    popoverId: pop.popoverId,
    _filterMeta,
  };
}

/* ── FilterButton component (spec §2 + §3) ────────────── */

export interface FilterButtonProps extends StyledProps, UnsafeProps, WithChildren, AllowedRest {
  /** Visible label for the chip. Required unless `ariaLabel` /
   *  `ariaLabelledBy` is provided. Truncates at 24ch with ellipsis. */
  label?: string;
  /** Selected filter value(s). Drives `data-applied` and the inline /
   *  count value rendering per `valueDisplay`. */
  value?: unknown;
  /** Callback invoked when the Clear (✕) sibling is activated. Also
   *  required for the Clear affordance to render. */
  onClear?: () => void;
  /** Open-state mirror passed by the adopter (or by `useFilterButton`
   *  via `aria-expanded`). Drives chevron rotation + open-border
   *  recipe. */
  popoverOpen?: boolean;
  /** Disable the trigger. Suppresses Clear sibling regardless of
   *  applied state. */
  disabled?: boolean;
  /** Size variant. Routes through ADR-045 `--control-h`. */
  size?: 'sm' | 'default' | 'lg';
  /** Override the value-display rule. `'inline'` → full value list,
   *  truncated; `'count'` → count badge; `'none'` → chrome-only;
   *  `'auto'` (default) → ≤2 inline / ≥3 count. */
  valueDisplay?: FilterButtonValueDisplay;
  /** Adopter escape hatch — overrides the kit's value rendering. */
  renderValue?: (values: readonly unknown[]) => ReactNode;
  /** Opt-in leading icon (default `undefined`). Adopters pass a glyph
   *  for high-signal cases (color swatches, calendar pickers, geo
   *  pins). Per amendment A12, no auto-funnel default. */
  leadingIcon?: ReactNode;
  /** Localized aria-label composer. */
  ariaLabelComposer?: (label: string, values: readonly unknown[]) => string;
  /** Accessible-name override. Wins when set even in applied states. */
  ariaLabel?: string;
  /** Id of an element whose text labels the trigger. */
  ariaLabelledBy?: string;
  /** ARIA id of the paired popover panel. When unset and `popoverOpen`
   *  is true, dev-warns once. */
  popoverId?: string;
  /** ARIA role of the paired popover. Used to derive `aria-haspopup`.
   *  Defaults to `'dialog'`. */
  panelRole?: 'dialog' | 'menu' | null;
  /** Click handler. The hook-supplied `onClick` (from `filterButtonProps`)
   *  takes precedence via rest-spread; adopters who don't use the hook
   *  wire this directly to their popover-open state. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** @internal Hook-resolved metadata. Spread by `useFilterButton()`
   *  via `filterButtonProps`. The component reads it to dev-warn when
   *  the adopter's component-side `value` / `onClear` props diverge
   *  from the hook's source-of-truth (spec §11 `meta-mismatch`). */
  _filterMeta?: FilterButtonMeta;
}

export const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  function FilterButton(
    {
      label,
      value,
      onClear,
      popoverOpen,
      disabled,
      size,
      valueDisplay,
      renderValue,
      leadingIcon,
      ariaLabelComposer,
      ariaLabel,
      ariaLabelledBy,
      popoverId,
      panelRole,
      className,
      unsafe_className,
      unsafe_style,
      onClick,
      children: _children,
      _filterMeta,
      ...rest
    },
    ref,
  ) {
    /* Pull `aria-haspopup`/`aria-expanded`/`aria-controls` off the rest
       bag BEFORE pickAllowed (which would otherwise let them through
       but also let `aria-haspopup="listbox"` etc. land — we need to
       sanitize and apply the right defaults). */
    const restRecord = rest as Record<string, unknown>;
    const rawAriaHaspopup = restRecord['aria-haspopup'];
    const rawAriaExpanded = restRecord['aria-expanded'];
    const rawAriaControls = restRecord['aria-controls'];
    // Remove them from the filtered bag so we control the values below.
    delete restRecord['aria-haspopup'];
    delete restRecord['aria-expanded'];
    delete restRecord['aria-controls'];

    const restProps = pickAllowed(restRecord) as React.HTMLAttributes<HTMLButtonElement>;

    const isApplied = computeIsApplied(value);
    const normalized = normalizeValues(value);
    const resolvedDisplay = resolveValueDisplay(valueDisplay, normalized);

    /* Resolve aria-expanded:
        - if adopter passed via rest (from useFilterButton) — use it
        - else derive from popoverOpen (when set)
        - else when aria-haspopup is set (i.e., this is a disclosure
          button) emit `false` so the APG disclosure pattern is
          satisfied. APG: a button with aria-haspopup expects
          aria-expanded to be present (true when open, false when
          closed).
        - else omit (panelRole=null escape hatch) */
    let ariaExpanded: boolean | undefined;
    if (typeof rawAriaExpanded === 'boolean') {
      ariaExpanded = rawAriaExpanded;
    } else if (typeof popoverOpen === 'boolean') {
      ariaExpanded = popoverOpen;
    } else if (panelRole !== null) {
      ariaExpanded = false;
    }
    const isOpen = ariaExpanded === true;

    /* Resolve aria-haspopup: prefer rest value (from hook), else derive
       from panelRole (default 'dialog'). null suppresses. */
    let ariaHaspopup: 'dialog' | 'menu' | undefined;
    if (typeof rawAriaHaspopup === 'string') {
      if (rawAriaHaspopup === 'dialog' || rawAriaHaspopup === 'menu') {
        ariaHaspopup = rawAriaHaspopup;
      }
    } else if (panelRole === null) {
      ariaHaspopup = undefined;
    } else if (panelRole === 'menu') {
      ariaHaspopup = 'menu';
    } else {
      ariaHaspopup = 'dialog';
    }

    /* Resolve aria-controls: prefer rest (from hook), else explicit
       popoverId prop. */
    const ariaControls =
      typeof rawAriaControls === 'string'
        ? rawAriaControls
        : typeof popoverId === 'string'
          ? popoverId
          : undefined;

    /* ── Dev-warnings ────────────────────────────────── */
    if (!IS_PROD) {
      /* Meta-mismatch (spec §11): when adopters use `useFilterButton()`
         AND also pass component-level `value` / `onClear` props, both
         pairs must match. Otherwise the hook-derived state (used for
         `aria-expanded`) diverges from the visible chip state (driven
         by the component's `value`). Same-reference equality is the
         default; arrays compare shallow-equal so `[…items]` rebuilds
         each render don't false-positive. */
      if (_filterMeta) {
        const hookValue = _filterMeta.value;
        const sameValue =
          hookValue === value ||
          (Array.isArray(hookValue) &&
            Array.isArray(value) &&
            hookValue.length === value.length &&
            hookValue.every((v, i) => v === value[i]));
        if (!sameValue) {
          devWarnOnce(
            'jkit.filter-button.meta-mismatch',
            '[JKit.FilterButton] `value` prop differs from `useFilterButton({ value })`. ' +
              "Both must match — the chip's visible state and the hook's " +
              'derived aria-expanded will diverge.',
          );
        } else if (_filterMeta.onClear !== onClear) {
          devWarnOnce(
            'jkit.filter-button.meta-mismatch',
            '[JKit.FilterButton] `onClear` prop differs from `useFilterButton({ onClear })`. ' +
              'Both must match — the Clear sibling fires the component-side ' +
              'handler while the hook holds a different reference.',
          );
        }
      }
      if (!label && !ariaLabel && !ariaLabelledBy) {
        devWarnOnce(
          'jkit.filter-button.missing-label-and-aria',
          '[JKit.FilterButton] requires either a visible `label` prop or an `ariaLabel` / `ariaLabelledBy` for screen-reader users.',
        );
      }
      if (isApplied && !onClear) {
        devWarnOnce(
          'jkit.filter-button.applied-without-onclear',
          '[JKit.FilterButton] has an applied value but no `onClear` handler. The Clear (✕) affordance will not be rendered; users have no way to clear the filter without opening the popover.',
        );
      }
      if (isOpen && !ariaControls) {
        devWarnOnce(
          'jkit.filter-button.dangling-aria-controls',
          '[JKit.FilterButton] has `popoverOpen=true` but no `aria-controls` attribute. Screen readers cannot locate the open popover. Use `useFilterButton()` or pass `popoverId` explicitly.',
        );
      }
      if (disabled && isOpen) {
        devWarnOnce(
          'jkit.filter-button.disabled-popover-open',
          "[JKit.FilterButton] received `disabled=true` while `popoverOpen=true`. The trigger will render as disabled (chevron stays at 0°); the popover state is the adopter's responsibility to reconcile.",
        );
      }
      if (label && label.length > 24) {
        devWarnOnce(
          'jkit.filter-button.label-overflow',
          '[JKit.FilterButton] label exceeds 24 characters and will truncate with ellipsis. Consider using `<Select>` for longer field-style labels.',
        );
      }
      if (valueDisplay === 'inline' && normalized.length > 0) {
        const joined = normalized.map((v) => String(v)).join(', ');
        if (joined.length > 16) {
          devWarnOnce(
            'jkit.filter-button.long-inline-value',
            "[JKit.FilterButton] inline value exceeds 16 characters and will truncate with ellipsis. Consider `valueDisplay='count'`.",
          );
        }
      }
      /* Localization dev-warn — fire only if the default composer is
         in use AND the document lang is non-`en*`. SSR-safe via the
         document guard. */
      if (
        typeof document !== 'undefined' &&
        !ariaLabelComposer &&
        document.documentElement.lang &&
        !/^en(\b|-)/i.test(document.documentElement.lang)
      ) {
        devWarnOnce(
          'jkit.filter-button.english-aria-composer',
          '[JKit.FilterButton] default `ariaLabelComposer` is English-only. Pass an `ariaLabelComposer` to `useFilterButton()` (or `<FilterButton>`) for proper localization.',
        );
      }
    }

    /* ── Accessible name composition (spec §7) ───────── */
    /* When `ariaLabel` is set it wins outright; when `label` is set
       AND the chip is applied, the composer builds the rich name
       ("Brand, filtered by Nike"); otherwise leave aria-label unset
       (the visible label-span is the accessible name via native
       button text content). */
    const composer = ariaLabelComposer ?? defaultAriaLabelComposer;
    const computedAriaLabel =
      ariaLabel ?? (label && isApplied ? composer(label, normalized) : undefined);

    /* ── Value-display rendering ────────────────────── */
    let valueNode: ReactNode = null;
    if (renderValue) {
      valueNode = renderValue(normalized);
    } else if (resolvedDisplay === 'inline' && normalized.length > 0) {
      const joined = normalized.map((v) => String(v)).join(', ');
      valueNode = (
        <>
          <span className="j-filter-btn-sep" aria-hidden="true">
            {/* Colon + NBSP (U+00A0) per spec §2 separator. */}:{' '}
          </span>
          <span className="j-filter-btn-value">{joined}</span>
        </>
      );
    } else if (resolvedDisplay === 'count' && normalized.length > 0) {
      valueNode = (
        <span className="j-filter-btn-value">
          <span className="j-filter-btn-count" aria-hidden="true">
            ({normalized.length})
          </span>
        </span>
      );
    }

    /* ── Click handler ─────────────────────────────── */
    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (disabled) return;
      onClick?.(e);
    };

    /* ── Clear sibling ─────────────────────────────── */
    const showClear = isApplied && !!onClear && !disabled;
    const handleClearClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      /* Stop propagation so a click on Clear does NOT bubble to the
         FilterButton parent / open the popover. */
      e.stopPropagation();
      onClear?.();
    };
    const handleClearKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
      /* Stop space/enter on Clear from bubbling — the native click
         already handles activation. */
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
      }
    };

    const triggerClasses = cx(
      'j-filter-btn',
      size === 'sm' && 'j-filter-btn-sm',
      size === 'lg' && 'j-filter-btn-lg',
      className,
      unsafe_className,
    );

    return (
      <span className="j-filter-btn-shell">
        <button
          ref={ref}
          type="button"
          {...restProps}
          className={triggerClasses}
          aria-haspopup={ariaHaspopup}
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          aria-label={computedAriaLabel}
          aria-labelledby={ariaLabelledBy}
          data-applied={isApplied ? 'true' : 'false'}
          data-popover-open={isOpen && !disabled ? 'true' : 'false'}
          data-size={size === 'sm' || size === 'lg' ? size : undefined}
          disabled={disabled}
          onClick={handleClick}
          style={unsafe_style}
        >
          {leadingIcon !== undefined ? (
            <span className="j-filter-btn-icon" aria-hidden="true">
              {leadingIcon}
            </span>
          ) : null}
          {label ? <span className="j-filter-btn-label">{label}</span> : null}
          {valueNode}
          <span className="j-filter-btn-chevron" aria-hidden="true">
            <JIcon intent="chevron-down" size={16} decorative />
          </span>
        </button>
        {showClear ? (
          <button
            type="button"
            className="j-filter-btn-clear"
            aria-label={`Clear ${label ?? ''} filter`.trim()}
            onClick={handleClearClick}
            onKeyDown={handleClearKeyDown}
          >
            <JIcon intent="close" size={12} decorative />
          </button>
        ) : null}
      </span>
    );
  },
);

/* ── FilterGroup container (spec §2 anatomy + §8 Layout) ── */

export interface FilterGroupProps extends StyledProps, UnsafeProps, WithChildren {
  /** Optional leading summary text (e.g., "3 filters applied"). */
  summary?: ReactNode;
  /** Optional trailing clear-all slot. */
  clearAll?: ReactNode;
  /** Layout mode. `'wrap'` (default) wraps to multiple lines;
   *  `'overflow-x'` keeps a single row with horizontal scroll-snap. */
  scroll?: 'wrap' | 'overflow-x';
  /** Accessible label for the group. */
  ariaLabel?: string;
}

export const FilterGroup = React.forwardRef<HTMLDivElement, FilterGroupProps>(function FilterGroup(
  {
    children,
    summary,
    clearAll,
    scroll = 'wrap',
    ariaLabel,
    className,
    unsafe_className,
    unsafe_style,
  },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cx(
        'j-filter-group',
        scroll === 'overflow-x' && 'j-filter-group-scroll-x',
        className,
        unsafe_className,
      )}
      role="group"
      aria-label={ariaLabel}
      style={unsafe_style}
    >
      {summary ? (
        <span className="j-filter-group-summary" aria-live="polite">
          {summary}
        </span>
      ) : null}
      {children}
      {clearAll ? <span className="j-filter-group-clear-all">{clearAll}</span> : null}
    </div>
  );
});
